import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function SalonPremiumSettingsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [privileges, setPrivileges] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configModal, setConfigModal] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState({ shop_id: '', monthly_price: '', max_members: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [privData, shopData] = await Promise.all([
        api.getAdminPremiumPrivileges(),
        api.getAdminShops(),
      ]);
      setPrivileges(Array.isArray(privData) ? privData : []);
      setShops(shopData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const shopById = (id) => shops.find(s => s.id === id);

  // Shops that don't have premium configured yet
  const configuredShopIds = new Set(privileges.map(p => p.shop_id));
  const unconfiguredShops = shops.filter(s => !configuredShopIds.has(s.id));

  const handleToggle = async (priv) => {
    try {
      await api.updateAdminPremiumPrivilege(priv.id, { enabled: !priv.enabled });
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const openConfig = (priv) => {
    setConfigModal(priv);
    setForm({
      shop_id: priv.shop_id,
      monthly_price: priv.monthly_price || '',
      max_members: priv.max_members || '',
    });
  };

  const saveConfig = async () => {
    try {
      await api.updateAdminPremiumPrivilege(configModal.id, {
        monthly_price: parseFloat(form.monthly_price),
        max_members: form.max_members ? parseInt(form.max_members) : null,
      });
      setConfigModal(null);
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const openAdd = () => {
    setAddModal(true);
    setForm({ shop_id: '', monthly_price: '', max_members: '' });
  };

  const saveAdd = async () => {
    if (!form.shop_id || !form.monthly_price) {
      showAlert(t('admin.fillRequired', 'Please fill in required fields.'));
      return;
    }
    try {
      await api.createAdminPremiumPrivilege(
        parseInt(form.shop_id),
        parseFloat(form.monthly_price),
        form.max_members ? parseInt(form.max_members) : null
      );
      setAddModal(false);
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const labelStyle = { display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.salonPremiumSettings', 'Salon Premium Settings')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        {unconfiguredShops.length > 0 && (
          <button style={S.btnOrange} onClick={openAdd}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>
            + {t('admin.addSalonPremium', 'Add Salon Premium')}
          </button>
        )}
      </div>

      {/* Add Modal */}
      {addModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{t('admin.configurePremium', 'Configure Premium Plan')}</h2>

            <label style={labelStyle}>{t('admin.salon', 'Salon')}</label>
            <select value={form.shop_id} onChange={e => setForm({ ...form, shop_id: e.target.value })} style={S.select}>
              <option value="">{t('admin.selectSalon', 'Select Salon')}</option>
              {unconfiguredShops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={labelStyle}>{t('admin.monthlyPrice', 'Monthly Price (EGP)')}</label>
            <input
              type="number" placeholder="0.00" value={form.monthly_price}
              onChange={e => setForm({ ...form, monthly_price: e.target.value })}
              style={S.input}
            />

            <label style={labelStyle}>{t('admin.maxMembers', 'Max Members (optional)')}</label>
            <input
              type="number" placeholder={t('admin.unlimited', 'Unlimited')} value={form.max_members}
              onChange={e => setForm({ ...form, max_members: e.target.value })}
              style={S.input}
            />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={saveAdd} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save', 'Save')}</button>
              <button onClick={() => setAddModal(false)} style={S.btnOutline}>{t('common.cancel', 'Cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {configModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{t('admin.configurePlan', 'Configure Plan')}</h2>

            <label style={labelStyle}>{t('admin.monthlyPrice', 'Monthly Price (EGP)')}</label>
            <input
              type="number" placeholder="0.00" value={form.monthly_price}
              onChange={e => setForm({ ...form, monthly_price: e.target.value })}
              style={S.input}
            />

            <label style={labelStyle}>{t('admin.maxMembers', 'Max Members (optional)')}</label>
            <input
              type="number" placeholder={t('admin.unlimited', 'Unlimited')} value={form.max_members}
              onChange={e => setForm({ ...form, max_members: e.target.value })}
              style={S.input}
            />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={saveConfig} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save', 'Save')}</button>
              <button onClick={() => setConfigModal(null)} style={S.btnOutline}>{t('common.cancel', 'Cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading', 'Loading...')}</p> : privileges.length === 0 ? (
        <p style={{ color: T.muted, fontStyle: 'italic' }}>{t('admin.noPremiumConfigured', 'No salons have premium configured yet.')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
          {privileges.map(priv => {
            const shop = shopById(priv.shop_id);
            return (
              <div key={priv.id} style={{
                ...S.card,
                borderTop: `3px solid ${priv.enabled ? T.orange : T.border}`,
                opacity: priv.enabled ? 1 : 0.7,
                transition: 'opacity 0.2s',
              }}>
                {/* Header: Name + Toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{
                      fontFamily: "'DM Sans', sans-serif", fontSize: '1.05rem', fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase', color: T.white, margin: 0,
                    }}>
                      {shop?.name || `Shop #${priv.shop_id}`}
                    </h3>
                  </div>
                  {/* Toggle Switch */}
                  <div
                    onClick={() => handleToggle(priv)}
                    style={{
                      width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer',
                      background: priv.enabled ? T.greenText : T.border,
                      position: 'relative', transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '3px',
                      left: priv.enabled ? '23px' : '3px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{
                    padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem',
                    fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase',
                    background: priv.enabled ? T.green : T.red,
                    color: priv.enabled ? T.greenText : T.redText,
                  }}>
                    {priv.enabled ? t('admin.premiumActive', 'Premium Active') : t('admin.premiumDisabled', 'Premium Disabled')}
                  </span>
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: T.white }}>{priv.active_members ?? 0}</div>
                    <div style={{ fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('admin.activeVIP', 'Active VIP')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: T.orange }}>{priv.monthly_price || 0}</div>
                    <div style={{ fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('admin.egpMonth', 'EGP/Mo')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: T.greenText }}>
                      {((priv.active_members || 0) * (priv.monthly_price || 0)).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('admin.revenue', 'Revenue')}
                    </div>
                  </div>
                </div>

                {/* Configure Button */}
                <button
                  onClick={() => openConfig(priv)}
                  style={{ ...S.btnOutline, width: '100%', textAlign: 'center' }}
                >
                  {t('admin.configurePlan', 'Configure Plan')}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

export default function SalonPremiumSettings() {
  return <ProtectedRoute requireSuperAdmin><SalonPremiumSettingsContent /></ProtectedRoute>;
}
